import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Alert } from '../../components/ui';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const PLANS = [
  { id: 'pack_one',      name: 'Pack One',      price: '€32',  period: '/month', contacts: '2,000',   sends: '6,000',   verify: '2,000',   accounts: '10' },
  { id: 'pack_two',      name: 'Pack Two',      price: '€78',  period: '/month', contacts: '30,000',  sends: '90,000',  verify: '30,000',  accounts: '100', popular: true },
  { id: 'pack_advanced', name: 'Pack Advanced', price: '€144', period: '/month', contacts: 'Unlimited',sends: '150,000', verify: '50,000',  accounts: 'Unlimited' },
];

const BANK = {
  accountHolder: 'MS DISCOVERIES',
  accountNumber: 'DE27202208000046114745',
  bic:           'SXPYDEHH',
  bankName:      'Banking Circle S.A - German Branch',
  bankAddress:   'Maximilianstraße 54, 80538 München',
  currency:      'EUR',
};

export default function Payment() {
  const { user, fetchMe } = useAuthStore();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);          // 1=choose plan, 2=bank details+reference
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState('');

  // If already active, redirect to dashboard
  useEffect(() => {
    if (user?.status === 'active') navigate('/client/dashboard');
  }, [user]);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const submitReference = async () => {
    if (!reference.trim()) return toast.error('Please enter your bank transaction / reference number');
    setSubmitting(true);
    try {
      await api.post('/payments/bank-transfer', {
        planId: selectedPlan.id,
        referenceNumber: reference.trim(),
        notes: notes.trim(),
        amount: selectedPlan.price,
      });
      setSubmitted(true);
      // refresh user so status reflects pending_verification
      await fetchMe();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to submit. Please try again.');
    } finally { setSubmitting(false); }
  };

  // ── SUBMITTED SCREEN ──────────────────────────────────────
  if (submitted) return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ textAlign: 'center', padding: '20px 0 10px' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(46,204,113,0.12)', border: '2px solid #2ECC71', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, margin: '0 auto 20px' }}>✅</div>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: '#2ECC71', marginBottom: 10 }}>Reference Submitted!</h2>
          <p style={{ color: '#888', fontSize: 14, lineHeight: 1.8, marginBottom: 24 }}>
            Your bank transfer reference has been sent to our admin team.<br />
            We will verify your payment and activate your account within <strong style={{ color: '#fff' }}>1–2 business hours</strong> during working hours<br />
            <span style={{ fontSize: 12, color: '#555' }}>(Monday – Saturday, 10:00 AM – 6:00 PM IST)</span>
          </p>
          <div style={{ background: '#1a1a1a', borderRadius: 12, padding: '14px 20px', marginBottom: 24, border: '1px solid rgba(46,204,113,0.15)' }}>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Your reference number</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#2ECC71' }}>{reference}</div>
          </div>
          <p style={{ color: '#555', fontSize: 13, marginBottom: 24 }}>
            You'll receive a confirmation once your account is activated.<br />
            For urgent queries: <a href="mailto:support@msdiscoveries.com" style={{ color: '#2ECC71' }}>support@msdiscoveries.com</a> · +91 9443724724
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <a href="mailto:support@msdiscoveries.com" style={{ padding: '10px 24px', border: '1px solid rgba(46,204,113,0.3)', borderRadius: 10, color: '#2ECC71', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>📧 Contact Support</a>
          </div>
        </div>
      </div>
      <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#444' }}>
        Already activated? <Link to="/client" style={{ color: '#2ECC71' }}>Login here</Link>
      </p>
    </div>
  );

  // ── STEP 1: CHOOSE PLAN ───────────────────────────────────
  if (step === 1) return (
    <div style={wrap}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontSize: 28, fontWeight: 900, color: '#2ECC71', letterSpacing: -1 }}>MARK</span>
          <span style={{ fontSize: 13, color: '#777', marginLeft: 8 }}>Ms Discoveries</span>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 900, marginBottom: 6 }}>Choose Your Plan</h1>
        <p style={{ color: '#666', fontSize: 14 }}>Select a plan to continue. Payment via bank transfer.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 16, maxWidth: 1000, margin: '0 auto 24px' }}>
        {PLANS.map(plan => (
          <div key={plan.id} onClick={() => setSelectedPlan(plan)}
            style={{ background: '#111', border: `2px solid ${selectedPlan?.id === plan.id ? '#2ECC71' : plan.popular ? 'rgba(46,204,113,0.3)' : 'rgba(46,204,113,0.1)'}`, borderRadius: 20, padding: 28, cursor: 'pointer', position: 'relative', transition: 'all 0.2s', boxShadow: selectedPlan?.id === plan.id ? '0 0 30px rgba(46,204,113,0.2)' : 'none' }}
            onMouseEnter={e => { if (selectedPlan?.id !== plan.id) e.currentTarget.style.borderColor = 'rgba(46,204,113,0.4)'; }}
            onMouseLeave={e => { if (selectedPlan?.id !== plan.id) e.currentTarget.style.borderColor = plan.popular ? 'rgba(46,204,113,0.3)' : 'rgba(46,204,113,0.1)'; }}>
            {plan.popular && <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#2ECC71', color: '#000', padding: '3px 16px', borderRadius: 20, fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' }}>MOST POPULAR</div>}
            {selectedPlan?.id === plan.id && <div style={{ position: 'absolute', top: 14, right: 14, width: 22, height: 22, borderRadius: '50%', background: '#2ECC71', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900, color: '#000' }}>✓</div>}
            <div style={{ fontSize: 11, fontWeight: 800, color: '#2ECC71', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>{plan.name}</div>
            <div style={{ fontSize: 40, fontWeight: 900, marginBottom: 4, letterSpacing: -1 }}>{plan.price}<span style={{ fontSize: 14, fontWeight: 400, color: '#555' }}>{plan.period}</span></div>
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[['✉️', 'Emails/mo', plan.emails], ['🔗', 'Accounts', plan.accounts], ['✅', 'Verify/mo', plan.verify], ['🌡️', 'Warmup', plan.warmup]].map(([icon, label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#666' }}>{icon} {label}</span>
                  <span style={{ color: '#2ECC71', fontWeight: 700 }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Custom plan note */}
      <div style={{ textAlign: 'center', marginBottom: 28, fontSize: 13, color: '#555' }}>
        Need a custom plan? <a href="mailto:support@msdiscoveries.com" style={{ color: '#2ECC71', fontWeight: 600 }}>Contact us →</a>
      </div>

      <div style={{ textAlign: 'center' }}>
        <Button size="lg" disabled={!selectedPlan} onClick={() => setStep(2)} style={{ minWidth: 260 }}>
          Continue with {selectedPlan?.name || 'a Plan'} →
        </Button>
      </div>
    </div>
  );

  // ── STEP 2: BANK DETAILS + REFERENCE ─────────────────────
  return (
    <div style={wrap}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontSize: 26, fontWeight: 900, color: '#2ECC71', letterSpacing: -1 }}>MARK</span>
          <span style={{ fontSize: 13, color: '#777', marginLeft: 8 }}>Ms Discoveries</span>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 900, marginBottom: 6 }}>Complete Bank Transfer</h1>
        <p style={{ color: '#666', fontSize: 14 }}>Transfer <strong style={{ color: '#fff' }}>{selectedPlan.price}</strong> to the account below, then paste your reference number.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 900, margin: '0 auto' }}>
        {/* Bank Details */}
        <div>
          <div style={{ background: '#111', border: '1px solid rgba(46,204,113,0.2)', borderRadius: 20, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ background: 'rgba(46,204,113,0.08)', padding: '16px 20px', borderBottom: '1px solid rgba(46,204,113,0.15)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#2ECC71', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🏦</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14 }}>Bank Transfer Details</div>
                  <div style={{ fontSize: 11, color: '#2ECC71' }}>Transfer exactly {selectedPlan.price}</div>
                </div>
              </div>
            </div>
            {/* Fields */}
            <div style={{ padding: '8px 0' }}>
              {[
                ['Account Holder', BANK.accountHolder],
                ['Account Number (IBAN)', BANK.accountNumber],
                ['BIC / SWIFT', BANK.bic],
                ['Bank Name', BANK.bankName],
                ['Bank Address', BANK.bankAddress],
                ['Currency', BANK.currency],
                ['Amount', selectedPlan.price + '/month'],
              ].map(([label, value]) => (
                <div key={label} style={{ padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,0.03)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#555', fontWeight: 600, marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 13, color: label === 'Account Number (IBAN)' || label === 'BIC / SWIFT' ? '#2ECC71' : '#ccc', fontWeight: label === 'Account Number (IBAN)' || label === 'BIC / SWIFT' ? 700 : 400, fontFamily: label === 'Account Number (IBAN)' || label === 'BIC / SWIFT' ? 'monospace' : 'inherit', letterSpacing: label === 'Account Number (IBAN)' ? 0.5 : 0 }}>{value}</div>
                  </div>
                  {(label === 'Account Number (IBAN)' || label === 'BIC / SWIFT') && (
                    <button onClick={() => copy(value, label)} style={{ background: copied === label ? 'rgba(46,204,113,0.2)' : 'rgba(255,255,255,0.04)', border: `1px solid ${copied === label ? 'rgba(46,204,113,0.4)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 7, color: copied === label ? '#2ECC71' : '#666', padding: '4px 10px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s', flexShrink: 0 }}>
                      {copied === label ? '✓ Copied' : 'Copy'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <Alert type="warning" style={{ marginTop: 14 }}>
            <strong>Important:</strong> Include your registered email in the transfer reference/notes so we can identify your payment quickly.
          </Alert>
        </div>

        {/* Reference form */}
        <div>
          {/* Selected plan summary */}
          <div style={{ background: 'rgba(46,204,113,0.06)', border: '1px solid rgba(46,204,113,0.2)', borderRadius: 14, padding: '14px 18px', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 11, color: '#2ECC71', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Selected Plan</div>
                <div style={{ fontSize: 18, fontWeight: 900 }}>{selectedPlan.name}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 26, fontWeight: 900, color: '#2ECC71' }}>{selectedPlan.price}</div>
                <div style={{ fontSize: 11, color: '#555' }}>{selectedPlan.period}</div>
              </div>
            </div>
            <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: '#555', fontSize: 12, cursor: 'pointer', marginTop: 8, padding: 0, textDecoration: 'underline' }}>← Change plan</button>
          </div>

          {/* Steps */}
          <div style={{ background: '#111', border: '1px solid rgba(46,204,113,0.12)', borderRadius: 16, padding: '18px 20px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#ccc', marginBottom: 14 }}>How to complete payment:</div>
            {[
              'Transfer the exact amount to the bank account on the left',
              'Use your registered email as payment reference',
              'After transfer, copy your bank transaction / reference number',
              'Paste it below and click Submit',
              'Our team will verify and activate your account',
            ].map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(46,204,113,0.15)', border: '1px solid rgba(46,204,113,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#2ECC71', flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                <div style={{ fontSize: 13, color: '#888', lineHeight: 1.5 }}>{step}</div>
              </div>
            ))}
          </div>

          {/* Reference input */}
          <div style={{ background: '#111', border: '1px solid rgba(46,204,113,0.15)', borderRadius: 16, padding: '20px' }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Bank Transaction / Reference Number</div>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 14 }}>Paste the reference or transaction ID from your bank after completing the transfer.</div>

            <div style={{ marginBottom: 14 }}>
              <input
                placeholder="e.g. TXN123456789 or FT240312345678"
                value={reference}
                onChange={e => setReference(e.target.value)}
                style={{ width: '100%', padding: '12px 14px', background: '#1a1a1a', border: `1px solid ${reference ? 'rgba(46,204,113,0.4)' : 'rgba(46,204,113,0.15)'}`, borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, outline: 'none', fontFamily: 'monospace', letterSpacing: 0.5, transition: 'border 0.2s' }} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 6 }}>Additional Notes (optional)</label>
              <textarea
                placeholder="Any additional info for the admin..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: '10px 14px', background: '#1a1a1a', border: '1px solid rgba(46,204,113,0.1)', borderRadius: 10, color: '#ccc', fontSize: 13, outline: 'none', resize: 'none', fontFamily: 'inherit' }} />
            </div>

            <Button fullWidth size="lg" loading={submitting} disabled={!reference.trim()} onClick={submitReference}>
              📤 Submit Reference Number
            </Button>

            <p style={{ fontSize: 11, color: '#444', textAlign: 'center', marginTop: 12, lineHeight: 1.7 }}>
              After submission, our admin will verify your payment and activate your account.<br />
              Questions? <a href="mailto:support@msdiscoveries.com" style={{ color: '#2ECC71' }}>support@msdiscoveries.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const wrap = { minHeight: '100vh', background: '#0A0A0A', color: '#fff', padding: '40px 20px' };
const card = { background: '#111', border: '1px solid rgba(46,204,113,0.15)', borderRadius: 24, padding: 40, maxWidth: 540, margin: '0 auto' };
